import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

// Set global axios timeout
axios.defaults.timeout = 30000;

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(
    localStorage.getItem('token') || sessionStorage.getItem('token')
  );
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      const status = error.response?.status;
      // Only drop the session when the token is actually invalid (401/403).
      // Transient errors (5xx, Cloudflare 520, network timeouts) keep the session.
      if (status === 401 || status === 403) {
        logout();
      }
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password, rememberMe = false) => {
    const response = await axios.post(`${API}/auth/login`, { email, password, remember_me: rememberMe });
    const { token: newToken, user: userData } = response.data;
    if (rememberMe) {
      // Long-lived session (30 days) that survives closing the browser.
      localStorage.setItem('token', newToken);
      localStorage.setItem('remember_me', '1');
      sessionStorage.removeItem('token');
    } else {
      // Session-only: signed out when the browser/tab is fully closed.
      sessionStorage.setItem('token', newToken);
      localStorage.removeItem('token');
      localStorage.removeItem('remember_me');
    }
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const register = async (email, password, name, inviteToken = null) => {
    const response = await axios.post(`${API}/auth/register`, {
      email,
      password,
      name,
      invite_token: inviteToken
    });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('token', newToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('remember_me');
    sessionStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
  };

  const refreshUser = async () => {
    if (token) {
      await fetchUser();
    }
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
